import { useCallback, useEffect, useState } from "react";
import api, { errorMessage } from "./api";
import { moveInList } from "./adminLogic";
import {
    AdminField,
    Card,
    ChoiceButtons,
    ConfirmButton,
    Empty,
    Feedback,
    PageTitle,
    PrimaryButton,
    SecondaryButton,
} from "./ui";
import { euro, SIDE_DISHES } from "../components/order/orderUi";

// Die vier Karten sind fest; Reihenfolge wie auf der Website.
const MENU_NAMES = ["Speisekarte", "Mittagskarte", "Weinkarte", "Bestellkarte"];

const bySortOrder = (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0);

const priceLabel = (prices) =>
    prices.map((p) => (p.description ? `${p.description} ${euro(p.amount)}` : euro(p.amount))).join(" · ");

// Neue sortOrder 0..n für alle Einträge, deren Position sich geändert hat.
const persistOrder = (list, path) =>
    Promise.all(
        list
            .map((entry, index) => ({ entry, index }))
            .filter(({ entry, index }) => entry.sortOrder !== index)
            .map(({ entry, index }) => api.put(`${path}/${entry._id}`, { sortOrder: index })),
    );

const MoveButtons = ({ onUp, onDown, label, isFirst, isLast, disabled }) => (
    <div className="flex gap-1">
        <button
            type="button"
            disabled={disabled || isFirst}
            onClick={onUp}
            aria-label={`${label} nach oben`}
            className="h-10 w-10 border border-forest/20 bg-transparent disabled:opacity-30"
        >
            ↑
        </button>
        <button
            type="button"
            disabled={disabled || isLast}
            onClick={onDown}
            aria-label={`${label} nach unten`}
            className="h-10 w-10 border border-forest/20 bg-transparent disabled:opacity-30"
        >
            ↓
        </button>
    </div>
);

const ItemEditor = ({ item, categories, onSave, onDelete, onCancel }) => {
    const [draft, setDraft] = useState(() => ({
        title: item.title || "",
        description: item.description || "",
        prices: item.prices && item.prices.length ? item.prices : [{ description: "", amount: "" }],
        category: item.category,
        sideDish: Boolean(item.sideDish),
        vegetarian: Boolean(item.vegetarian),
    }));
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);

    const setPrice = (index, field, value) =>
        setDraft((d) => ({
            ...d,
            prices: d.prices.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
        }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMessage(null);
        try {
            await onSave({
                ...draft,
                title: draft.title.trim(),
                prices: draft.prices.map((p) => ({ description: p.description.trim(), amount: Number(p.amount) })),
            });
        } catch (err) {
            setMessage({ error: true, text: errorMessage(err, "Speichern fehlgeschlagen.") });
            setBusy(false);
        }
    };

    return (
        <Card className="p-5">
            <form onSubmit={submit} className="grid gap-[18px]">
                <div className="flex items-center justify-between">
                    <h2 className="m-0 font-caslon text-[22px] font-normal">
                        {item._id ? "Gericht bearbeiten" : "Neues Gericht"}
                    </h2>
                    <button type="button" onClick={onCancel} className="bg-transparent py-2 text-[13px] underline">
                        Abbrechen
                    </button>
                </div>
                <AdminField
                    label="Titel"
                    required
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                />
                <AdminField
                    label="Beschreibung"
                    as="textarea"
                    rows={2}
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />
                <fieldset className="m-0 grid gap-2 border-0 p-0">
                    <legend className="mb-2 text-xs uppercase tracking-[.12em] text-forest/60">Preise</legend>
                    {draft.prices.map((price, index) => (
                        <div key={index} className="grid grid-cols-[minmax(0,1fr)_110px_44px] gap-2">
                            <input
                                className="admin-input"
                                aria-label={`Preis ${index + 1}: Bezeichnung`}
                                placeholder="z.B. 0,2 l (optional)"
                                value={price.description}
                                onChange={(e) => setPrice(index, "description", e.target.value)}
                            />
                            <input
                                className="admin-input text-right"
                                aria-label={`Preis ${index + 1}: Betrag in Euro`}
                                type="number"
                                step="0.01"
                                min="0"
                                required
                                value={price.amount}
                                onChange={(e) => setPrice(index, "amount", e.target.value)}
                            />
                            <button
                                type="button"
                                aria-label={`Preis ${index + 1} entfernen`}
                                disabled={draft.prices.length === 1}
                                onClick={() =>
                                    setDraft((d) => ({ ...d, prices: d.prices.filter((_, i) => i !== index) }))
                                }
                                className="border border-forest/20 bg-transparent text-lg disabled:opacity-30"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() =>
                            setDraft((d) => ({ ...d, prices: [...d.prices, { description: "", amount: "" }] }))
                        }
                        className="justify-self-start bg-transparent py-2 text-[13px] underline"
                    >
                        + weiterer Preis
                    </button>
                </fieldset>
                <AdminField
                    label="Kategorie"
                    as="select"
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                >
                    {categories.map((c) => (
                        <option key={c._id} value={c._id}>
                            {c.title}
                        </option>
                    ))}
                </AdminField>
                <label className="flex min-h-[44px] items-center gap-3 text-[15px]">
                    <input
                        type="checkbox"
                        checked={draft.sideDish}
                        onChange={(e) => setDraft({ ...draft, sideDish: e.target.checked })}
                        className="h-[22px] w-[22px] accent-forest"
                    />
                    Beilage wählbar ({SIDE_DISHES.join(", ")})
                </label>
                <label className="flex min-h-[44px] items-center gap-3 text-[15px]">
                    <input
                        type="checkbox"
                        checked={draft.vegetarian}
                        onChange={(e) => setDraft({ ...draft, vegetarian: e.target.checked })}
                        className="h-[22px] w-[22px] accent-forest"
                    />
                    Vegetarisch (Kennzeichnung auf der Website)
                </label>
                <Feedback message={message} />
                <div className={`grid gap-2.5 ${item._id ? "grid-cols-2" : ""}`}>
                    <PrimaryButton type="submit" disabled={busy}>
                        Speichern
                    </PrimaryButton>
                    {item._id && (
                        <ConfirmButton onConfirm={onDelete} className="min-h-[52px] text-sm">
                            Löschen
                        </ConfirmButton>
                    )}
                </div>
            </form>
        </Card>
    );
};

const CategoryBlock = ({
    category,
    items,
    index,
    count,
    busy,
    onMoveCategory,
    onMoveItem,
    onEdit,
    onAdd,
    onRename,
    onDelete,
}) => {
    const [renaming, setRenaming] = useState(false);
    const [title, setTitle] = useState(category.title);

    return (
        <section>
            <div className="mb-1 flex items-center justify-between gap-3 border-b border-forest pb-2">
                {renaming ? (
                    <form
                        className="flex flex-1 gap-2"
                        onSubmit={async (e) => {
                            e.preventDefault();
                            await onRename(category, title.trim());
                            setRenaming(false);
                        }}
                    >
                        <input
                            className="admin-input !py-2"
                            aria-label="Kategoriename"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            autoFocus
                        />
                        <SecondaryButton type="submit">OK</SecondaryButton>
                    </form>
                ) : (
                    <button
                        type="button"
                        onClick={() => setRenaming(true)}
                        title="Umbenennen"
                        className="bg-transparent text-left font-caslon text-xl"
                    >
                        {category.title}
                    </button>
                )}
                <MoveButtons
                    label={category.title}
                    isFirst={index === 0}
                    isLast={index === count - 1}
                    disabled={busy}
                    onUp={() => onMoveCategory(index, -1)}
                    onDown={() => onMoveCategory(index, 1)}
                />
            </div>
            {items.map((item, itemIndex) => (
                <div
                    key={item._id}
                    className="grid min-h-[44px] grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-forest/10 py-2"
                >
                    <button
                        type="button"
                        onClick={() => onEdit(item)}
                        className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 bg-transparent text-left"
                    >
                        <span className="text-base">{item.title}</span>
                        <span className="text-[15px] tabular-nums">{priceLabel(item.prices)}</span>
                        {(item.description || item.sideDish || item.vegetarian) && (
                            <span className="col-span-full text-[13px] text-forest/55">
                                {[item.description, item.sideDish && "mit Beilage", item.vegetarian && "vegetarisch"]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </span>
                        )}
                    </button>
                    <MoveButtons
                        label={item.title}
                        isFirst={itemIndex === 0}
                        isLast={itemIndex === items.length - 1}
                        disabled={busy}
                        onUp={() => onMoveItem(category._id, itemIndex, -1)}
                        onDown={() => onMoveItem(category._id, itemIndex, 1)}
                    />
                </div>
            ))}
            <button
                type="button"
                onClick={() => onAdd(category)}
                className="mt-2 min-h-[44px] w-full border border-dashed border-forest/35 bg-transparent p-3 text-sm text-forest/70"
            >
                + Gericht in „{category.title}“
            </button>
            {items.length === 0 && (
                <ConfirmButton onConfirm={() => onDelete(category)} className="mt-2">
                    Leere Kategorie löschen
                </ConfirmButton>
            )}
        </section>
    );
};

export default function MenusPage() {
    const [menus, setMenus] = useState([]);
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [activeName, setActiveName] = useState(MENU_NAMES[0]);
    const [editing, setEditing] = useState(null);
    const [newCategory, setNewCategory] = useState("");
    const [message, setMessage] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        const [m, c, i] = await Promise.all([api.get("/menus"), api.get("/categories"), api.get("/items")]);
        setMenus(m.data);
        setCategories(c.data);
        setItems(i.data);
        setLoaded(true);
    }, []);

    useEffect(() => {
        load().catch(() => setLoaded(true));
    }, [load]);

    const activeMenu = menus.find((m) => m.name === activeName);
    const menuCategories = categories
        .filter((c) => activeMenu && c.menu === activeMenu._id)
        .sort(bySortOrder);
    const itemsOf = (categoryId) => items.filter((i) => i.category === categoryId).sort(bySortOrder);

    // Änderung ausführen und neu laden; währenddessen sind die Pfeile gesperrt.
    const run = async (action, fallback) => {
        setMessage(null);
        setBusy(true);
        try {
            await action();
            await load();
        } catch (err) {
            setMessage({ error: true, text: errorMessage(err, fallback) });
        } finally {
            setBusy(false);
        }
    };

    const moveCategory = (index, dir) =>
        run(() => persistOrder(moveInList(menuCategories, index, dir), "/categories"), "Sortieren fehlgeschlagen.");

    const moveItem = (categoryId, index, dir) =>
        run(() => persistOrder(moveInList(itemsOf(categoryId), index, dir), "/items"), "Sortieren fehlgeschlagen.");

    const saveItem = async (draft) => {
        const original = editing;
        const movedCategory = !original._id || original.category !== draft.category;
        const payload = movedCategory ? { ...draft, sortOrder: itemsOf(draft.category).length } : draft;
        if (original._id) {
            await api.put(`/items/${original._id}`, payload);
        } else {
            await api.post("/items", payload);
        }
        setEditing(null);
        await load();
    };

    const deleteItem = () =>
        run(async () => {
            await api.delete(`/items/${editing._id}`);
            setEditing(null);
        }, "Löschen fehlgeschlagen.");

    const addCategory = (e) => {
        e.preventDefault();
        const title = newCategory.trim();
        if (!title || !activeMenu) return;
        run(async () => {
            await api.post(`/categories/${activeMenu._id}`, { title, sortOrder: menuCategories.length });
            setNewCategory("");
        }, "Kategorie konnte nicht angelegt werden.");
    };

    const renameCategory = (category, title) =>
        run(() => api.put(`/categories/${category._id}`, { title }), "Umbenennen fehlgeschlagen.");

    const deleteCategory = (category) =>
        run(() => api.delete(`/categories/${category._id}`), "Löschen fehlgeschlagen.");

    return (
        <div>
            <PageTitle>Speisekarten</PageTitle>
            <ChoiceButtons
                label="Karte"
                options={MENU_NAMES.map((name) => [name, name])}
                value={activeName}
                onChange={(name) => {
                    setActiveName(name);
                    setEditing(null);
                }}
            />
            <Feedback message={message} />

            {editing ? (
                <ItemEditor
                    item={editing}
                    categories={menuCategories}
                    onSave={saveItem}
                    onDelete={deleteItem}
                    onCancel={() => setEditing(null)}
                />
            ) : (
                <div className="mt-2 grid gap-6">
                    {loaded && !activeMenu && <Empty>Die Karte „{activeName}“ gibt es in der Datenbank nicht.</Empty>}
                    {menuCategories.map((category, index) => (
                        <CategoryBlock
                            key={category._id}
                            category={category}
                            items={itemsOf(category._id)}
                            index={index}
                            count={menuCategories.length}
                            busy={busy}
                            onMoveCategory={moveCategory}
                            onMoveItem={moveItem}
                            onEdit={setEditing}
                            onAdd={(c) => setEditing({ category: c._id })}
                            onRename={renameCategory}
                            onDelete={deleteCategory}
                        />
                    ))}
                    {activeMenu && (
                        <form onSubmit={addCategory} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2.5">
                            <input
                                className="admin-input"
                                aria-label="Name der neuen Kategorie"
                                placeholder="Neue Kategorie"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                            />
                            <SecondaryButton type="submit" disabled={!newCategory.trim()}>
                                + Anlegen
                            </SecondaryButton>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
}
